import { useState } from 'react';

import { appColors, HighlightableText, Tier } from '@backrs_ui/common-ui';

import { SchoolLevel } from '@backrs_ui/grow-ui/src/models/models';
import { RESULT_PAGE_TOUR_IDS } from '@backrs_ui/grow-ui/src/pages/Result/tour/ResultPage.tour';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { markersOfSuccess } from './markersOfSuccess';
import { getCorrectMarkersOfSuccessItems } from './markersOfSuccess.util';

const BulletPointItems = ({
  lineItem,
}: {
  lineItem: { title?: string; description: string }[];
}) => {
  const additionalDictionary = useAppSelector((state) => state.dataDictionary.dataDictionary);

  return (
    <Box sx={listItemContainerStyle}>
      <ul style={{ paddingLeft: '0px' }}>
        {lineItem.map((bulletPoint, index) => (
          <li key={index}>
            <Typography variant="body1" style={markerOfSuccessLineItem}>
              {bulletPoint.title ? <strong>{bulletPoint.title} </strong> : ''}
              <HighlightableText
                text={bulletPoint.description}
                textVariant="body1"
                style={markerOfSuccessLineItem}
                additionalDictionary={additionalDictionary || undefined}
              />
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

type MarkersOfSuccessExpandableGroupProps = Readonly<{
  tier: Tier;
  schoolLevel: SchoolLevel;
  /** only used for the tour since the result page tour needs to control the opening/closing */
  expandedMarkersOfSuccessIndex: number | null;
}>;

export const MarkersOfSuccessExpandableGroup = ({
  tier,
  schoolLevel,
  expandedMarkersOfSuccessIndex,
}: MarkersOfSuccessExpandableGroupProps) => {
  const [expandedKeys, setExpandedKeys] = useState<string[] | null>(null);

  const items = getCorrectMarkersOfSuccessItems({ tier, schoolLevel, markersOfSuccess });

  if (!items) {
    return null;
  }

  const handleExpandClick = (key: string) => {
    setExpandedKeys((prevExpandedKeys) => {
      if (prevExpandedKeys === null) {
        return [key];
      }

      if (prevExpandedKeys.includes(key)) {
        return prevExpandedKeys.filter((k) => k !== key);
      }

      return [...prevExpandedKeys, key];
    });
  };

  return (
    <>
      {Object.entries(items).map(([key, value], index) => (
        <Box key={key} sx={itemContainerStyle} id={`${RESULT_PAGE_TOUR_IDS.step8}${index}`}>
          <Box sx={fullRowContainerStyle} onClick={() => handleExpandClick(key)}>
            <Typography variant="h6" sx={itemTitleStyle}>
              {key}
            </Typography>
            <Typography variant="h6" sx={itemTitleStyle}>
              {expandedKeys?.includes(key) ||
              (expandedKeys === null && index === expandedMarkersOfSuccessIndex)
                ? '-'
                : '+'}
            </Typography>
          </Box>
          {(expandedKeys?.includes(key) ||
            (expandedKeys === null && index === expandedMarkersOfSuccessIndex)) && (
            <BulletPointItems lineItem={value} />
          )}
        </Box>
      ))}
    </>
  );
};

const itemContainerStyle: SxProps<Theme> = {
  marginTop: '1rem',
};

const fullRowContainerStyle: SxProps<Theme> = {
  cursor: 'pointer',
  borderBottom: `2px solid ${appColors.color.blue1}`,
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  paddingRight: 1,
};

const itemTitleStyle: SxProps<Theme> = {
  fontWeight: 600,
  color: appColors.color.blue1,
  lineHeight: '1.25',
  fontSize: '18px',
  pb: 0.5,
};

const listItemContainerStyle: SxProps<Theme> = {
  mt: 2,
  ml: 2.25,
};

const markerOfSuccessLineItem: React.CSSProperties = {
  lineHeight: '1.3',
} as const;
